import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { VideoPost } from '../../models/e621.models';

interface TagInfo {
  name: string;
  category: string;
  cssClass: string;
  visible: boolean;
}

@Component({
  selector: 'app-tags',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tags.component.html',
  styleUrl: './tags.component.scss',
})
export class TagsComponent implements OnInit, OnChanges {
  @Input() video: VideoPost | null = null;
  @Output() tagClick = new EventEmitter<string>();

  searchQuery = '';
  allTags: TagInfo[] = [];
  filteredTags: Record<string, TagInfo[]> = {};

  // Category configuration
  categories = [
    { key: 'artist', label: '🎨 Artists', cssClass: 'tag-artist' },
    { key: 'character', label: '👤 Characters', cssClass: 'tag-character' },
    { key: 'species', label: '🐾 Species', cssClass: 'tag-species' },
    { key: 'copyright', label: '©️ Copyright', cssClass: 'tag-copyright' },
    { key: 'general', label: '🏷️ General', cssClass: 'tag-general' },
    { key: 'meta', label: '🔧 Meta', cssClass: 'tag-meta' },
    { key: 'lore', label: '📚 Lore', cssClass: 'tag-lore' },
  ];

  ngOnInit(): void {
    this.updateTags();
  }

  ngOnChanges(): void {
    this.updateTags();
  }

  private updateTags(): void {
    if (!this.video?.tagsByCategory) {
      this.allTags = [];
      this.filteredTags = {};
      return;
    }

    // Convert all tags to TagInfo objects
    this.allTags = [];
    this.categories.forEach(category => {
      // Type-safe access to tagsByCategory
      const tagsByCategory = this.video!.tagsByCategory;
      let tags: string[] = [];
      
      switch (category.key) {
        case 'artist':
          tags = tagsByCategory.artist || [];
          break;
        case 'character':
          tags = tagsByCategory.character || [];
          break;
        case 'species':
          tags = tagsByCategory.species || [];
          break;
        case 'copyright':
          tags = tagsByCategory.copyright || [];
          break;
        case 'general':
          tags = tagsByCategory.general || [];
          break;
        case 'meta':
          tags = tagsByCategory.meta || [];
          break;
        case 'lore':
          tags = tagsByCategory.lore || [];
          break;
      }
      
      tags.forEach((tagName: string) => {
        this.allTags.push({
          name: tagName,
          category: category.key,
          cssClass: category.cssClass,
          visible: true
        });
      });
    });

    this.filterTags();
  }

  onSearchChange(): void {
    this.filterTags();
  }

  private filterTags(): void {
    if (!this.searchQuery.trim()) {
      // No search - show all tags
      this.allTags.forEach(tag => tag.visible = true);
    } else {
      // Apply search filter
      const searchTerms = this.parseSearchQuery(this.searchQuery.toLowerCase().trim());
      
      this.allTags.forEach(tag => {
        tag.visible = this.matchesSearch(tag.name.toLowerCase(), searchTerms);
      });
    }

    // Group filtered tags by category
    this.filteredTags = {};
    this.categories.forEach(category => {
      this.filteredTags[category.key] = this.allTags.filter(
        tag => tag.category === category.key && tag.visible
      );
    });
  }

  private parseSearchQuery(query: string): string[] {
    // Split search query by spaces and also add underscore variants
    const terms = query.split(/\s+/).filter(term => term.length > 0);
    const allTerms = [...terms];
    
    // Add underscore-joined versions
    if (terms.length > 1) {
      allTerms.push(terms.join('_'));
    }
    
    // Add space-separated versions of underscore terms
    terms.forEach(term => {
      if (term.includes('_')) {
        allTerms.push(...term.split('_'));
        allTerms.push(term.replace(/_/g, ' '));
      }
    });

    return [...new Set(allTerms)]; // Remove duplicates
  }

  private matchesSearch(tagName: string, searchTerms: string[]): boolean {
    // Check if tag matches any of the search terms
    return searchTerms.some(term => {
      // Direct match
      if (tagName.includes(term)) {
        return true;
      }
      
      // Check underscore/space variants
      const tagWithSpaces = tagName.replace(/_/g, ' ');
      const tagWithUnderscores = tagName.replace(/\s+/g, '_');
      const termWithSpaces = term.replace(/_/g, ' ');
      const termWithUnderscores = term.replace(/\s+/g, '_');
      
      return tagWithSpaces.includes(term) ||
             tagWithSpaces.includes(termWithSpaces) ||
             tagWithUnderscores.includes(term) ||
             tagWithUnderscores.includes(termWithUnderscores) ||
             tagName.includes(termWithSpaces) ||
             tagName.includes(termWithUnderscores);
    });
  }

  onTagClick(tag: string): void {
    this.tagClick.emit(tag);
  }

  getCategoryLabel(categoryKey: string): string {
    const category = this.categories.find(c => c.key === categoryKey);
    if (categoryKey === 'general' && this.video?.tagsByCategory.general) {
      return `🏷️ General (${this.video.tagsByCategory.general.length})`;
    }
    return category?.label || categoryKey;
  }

  hasTags(categoryKey: string): boolean {
    return (this.filteredTags[categoryKey]?.length || 0) > 0;
  }
}